<template>
  <v-dialog :value="value" max-width="1000" scrollable persistent @input="$emit('input', $event)">
    <v-card>
      <v-card-title>
        {{ formTitle }}
      </v-card-title>

      <v-card-text>
        <ValidationObserver ref="formObserver">
          <v-form :disabled="isLoading">
            <v-container>
              <v-row>
                <v-col cols="12" sm="6">
                  <vee-text-field v-model="assessment.name" rules="required|max:128" label="Name*" />
                </v-col>

                <v-col cols="12" sm="6">
                  <v-checkbox v-model="assessment.active" label="Active" />
                </v-col>

                <v-col cols="12">
                  <vee-assessment-autocomplete
                    v-model="assessment.exercises"
                    :loading="areExercisesLoading"
                    :items="filteredExercises"
                    label="Assessments*"
                    rules="required|arrayRequired"
                    name="exercises"
                    :error-messages="showSessionsError ? ['Required'] : []"
                    :menu-props="{ maxHeight: '400px', closeOnContentClick: false }"
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </ValidationObserver>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="onCancel">
          Cancel
        </v-btn>
        <v-btn text :loading="isLoading" @click="onSave">
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mdiDeleteOutline } from '@mdi/js'
import {
  computed, onMounted, ref, watch, nextTick,
} from 'vue'
import { ValidationObserver } from 'vee-validate'
import { SessionType } from '@/enums/enums'
import useGolfAssessments from '@/composables/api/assessments/golf'
import VeeAssessmentAutocomplete from '@/components/veeInputs/VeeAssessmentAutocomplete.vue'

export default {
  components: {
    ValidationObserver,
    VeeAssessmentAutocomplete,
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    item: {
      required: true,
      type: Object,
    },
    value: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const { golfAssessments, isLoading: areGolfAssessmentsLoading, fetchGolfAssessments } = useGolfAssessments()

    const assessment = ref({
      exercises: [],
      ...props.item,
    })

    const showSessionsError = ref(false)
    const formObserver = ref(null)

    const formTitle = computed(() => {
      if (!assessment.value.id || assessment.value.id === '') {
        return 'Create Golf Skills Assessment'
      }

      return 'Edit Golf Skills Assessment'
    })

    const filteredExercises = golfAssessments

    const sessions = computed(() => buildAssessmentPayload(assessment.value).sessions || [])

    watch(() => assessment.value.exercises, newVal => {
      if (newVal && newVal.length > 0) {
        showSessionsError.value = false
      }
    })

    onMounted(async () => {
      await fetchGolfAssessments()

      if (assessment.value.active === undefined) {
        assessment.value.active = true
      }
      assessment.value.category = 'practice'

      let extracted = []
      if (props.item.sessions && Array.isArray(props.item.sessions)) {
        extracted = props.item.sessions.flatMap(s => {
          if (s.assessments && Array.isArray(s.assessments)) return s.assessments
          if (s.exercises && Array.isArray(s.exercises)) return s.exercises
          if (s.uniqueKey) return [s]

          return []
        })
      } else if (props.item.exercises && Array.isArray(props.item.exercises)) {
        extracted = props.item.exercises
      }

      if (extracted.length > 0) {
        const allExercises = golfAssessments.value || []

        assessment.value.exercises = extracted.map(existing => {
          const uKey = typeof existing === 'string' ? existing : (existing.uniqueKey || existing.id)
          const found = allExercises.find(e => e.uniqueKey === uKey
            || (e.exercise && e.exercise.id === uKey)
            || e.id === uKey)

          return found ? {
            ...found,
            ...(typeof existing === 'object' ? existing : {}),
            uniqueKey: found.uniqueKey, // Autocomplete safeguard
          } : existing
        }).filter(e => e && typeof e === 'object' && e.uniqueKey)
      }
    })

    function buildAssessmentPayload(formAssessment) {
      const grouped = {}
      const list = (formAssessment.exercises || []).filter(a => a && a.uniqueKey)

      for (const item of list) {
        const sType = item.sessionType || item.type || item.category || 'general'
        if (!grouped[sType]) {
          grouped[sType] = {
            category: formAssessment.category,
            type: sType,
            assessments: [],
          }
        }
        grouped[sType].assessments.push({ ...item })
      }

      return {
        id: formAssessment.id,
        name: formAssessment.name,
        category: formAssessment.category,
        companyId: formAssessment.companyId || null,
        sessions: Object.values(grouped),
        userId: formAssessment.userId || null,
        active: formAssessment.active ?? true,
      }
    }

    const onCancel = () => {
      emit('cancel')
    }

    const onSave = async () => {
      await nextTick()
      const isValid = await formObserver.value.validate()
      if (isValid && sessions.value.length > 0) {
        showSessionsError.value = false
        const payload = buildAssessmentPayload(assessment.value)
        emit('save', payload)
      } else if (sessions.value.length === 0) {
        showSessionsError.value = true
      }
    }

    const removeExercise = item => {
      const index = assessment.value.exercises.findIndex(value => item.uniqueKey === value.uniqueKey)
      if (index >= 0) {
        assessment.value.exercises.splice(index, 1)
      }
    }

    return {
      formObserver,
      assessment,
      areExercisesLoading: areGolfAssessmentsLoading,
      filteredExercises,
      onCancel,
      onSave,
      removeExercise,
      SessionType,
      formTitle,
      icons: {
        mdiDeleteOutline,
      },
      showSessionsError,
      sessions,
    }
  },
}
</script>
